/*
  The Ranch Gallery — <upload-details>
  - Collects up to N photos (drag & drop or file picker) and mirrors them into
    hidden, form-associated file inputs (properties[Photo 1..N]) so they submit
    as line item properties. Empty slots stay disabled so no empty property is
    created.
  - Keeps a live "X of N files" counter and a removable file list.
  - Recalculates its step number from the variant picker's
    data-total-variant-steps, so it always follows the last visible variant step.
*/
if (!customElements.get('upload-details')) {
  customElements.define(
    'upload-details',
    class UploadDetails extends HTMLElement {
      connectedCallback() {
        this.maxFiles = parseInt(this.dataset.maxFiles, 10) || 8;
        this.files = [];

        this.dropzone = this.querySelector('[data-dropzone]');
        this.picker = this.querySelector('[data-picker]');
        this.chooseBtn = this.querySelector('[data-choose]');
        this.counter = this.querySelector('[data-counter]');
        this.fileList = this.querySelector('[data-file-list]');
        this.slots = Array.from(this.querySelectorAll('[data-file-slot]'));
        this.notes = this.querySelector('[data-notes]');
        this.numberEl = this.querySelector('[data-upload-step-number]');

        this.bindUpload();
        this.bindNotes();
        this.renderCounter();

        this.updateStepNumber();
        this.observeSteps();
      }

      disconnectedCallback() {
        this.stepObserver?.disconnect();
      }

      /* ---------- Step numbering ---------- */
      observeSteps() {
        const scope = this.closest('product-info') || document.body;
        this.stepObserver = new MutationObserver(() => {
          window.requestAnimationFrame(() => this.updateStepNumber());
        });
        this.stepObserver.observe(scope, { childList: true, subtree: true });
      }

      updateStepNumber() {
        if (!this.numberEl) return;
        const marker = document.querySelector('variant-selects [data-total-variant-steps]');
        const total = marker ? parseInt(marker.getAttribute('data-total-variant-steps'), 10) : 0;
        this.numberEl.textContent = (Number.isNaN(total) ? 0 : total) + 1;
      }

      /* ---------- File upload ---------- */
      bindUpload() {
        if (!this.picker) return;

        this.chooseBtn?.addEventListener('click', () => this.picker.click());
        this.dropzone?.addEventListener('click', (e) => {
          if (e.target === this.chooseBtn) return;
          this.picker.click();
        });
        this.dropzone?.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.picker.click();
          }
        });

        this.picker.addEventListener('change', () => {
          this.addFiles(this.picker.files);
          this.picker.value = '';
        });

        ['dragenter', 'dragover'].forEach((type) =>
          this.dropzone?.addEventListener(type, (e) => {
            e.preventDefault();
            this.dropzone.classList.add('is-dragover');
          })
        );
        ['dragleave', 'dragend', 'drop'].forEach((type) =>
          this.dropzone?.addEventListener(type, (e) => {
            e.preventDefault();
            this.dropzone.classList.remove('is-dragover');
          })
        );
        this.dropzone?.addEventListener('drop', (e) => {
          if (e.dataTransfer?.files?.length) this.addFiles(e.dataTransfer.files);
        });
      }

      addFiles(fileListLike) {
        const incoming = Array.from(fileListLike).filter((f) => f.type.startsWith('image/'));
        for (const file of incoming) {
          if (this.files.length >= this.maxFiles) break;
          // Skip obvious duplicates (same name + size).
          const dup = this.files.some((f) => f.name === file.name && f.size === file.size);
          if (!dup) this.files.push(file);
        }
        this.syncInputs();
        this.renderFileList();
        this.renderCounter();
      }

      removeFile(index) {
        this.files.splice(index, 1);
        this.syncInputs();
        this.renderFileList();
        this.renderCounter();
      }

      syncInputs() {
        this.slots.forEach((input, i) => {
          if (i < this.files.length) {
            const dt = new DataTransfer();
            dt.items.add(this.files[i]);
            input.files = dt.files;
            input.disabled = false;
          } else {
            input.files = new DataTransfer().files;
            input.disabled = true;
          }
        });
      }

      renderFileList() {
        if (!this.fileList) return;
        this.fileList.innerHTML = '';
        this.files.forEach((file, i) => {
          const li = document.createElement('li');
          li.className = 'upload-details__file';

          const name = document.createElement('span');
          name.className = 'upload-details__file-name';
          name.textContent = file.name;

          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'upload-details__file-remove';
          remove.setAttribute('aria-label', `Remove ${file.name}`);
          remove.textContent = '×';
          remove.addEventListener('click', () => this.removeFile(i));

          li.append(name, remove);
          this.fileList.appendChild(li);
        });
      }

      renderCounter() {
        if (this.counter) this.counter.textContent = `${this.files.length} of ${this.maxFiles} files`;
      }

      /* ---------- Notes ---------- */
      bindNotes() {
        // Left always enabled and editable. A blank "Notes" property on an
        // order with no notes is harmless, and disabling the field to avoid
        // it made it unusable — don't repeat that mistake.
      }
    }
  );
}
